import { __ } from '@wordpress/i18n';

const Dashboard = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-8">
            {/* Hero Section */}
            <header className="bg-gradient-to-br from-purple-500 to-blue-600 text-white rounded-xl p-10 shadow-lg mb-10">
                <div className="max-w-5xl mx-auto text-center">
                <h1 className="text-5xl !text-5xl font-extrabold mb-6 leading-tight">
                    { __( 'Transform WordPress into a React-Like App', 'instant-page-load' ) }
                    <br />
                    <span className="text-3xl bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                        { __( '(0ms Delays • Preserved Scroll • No Coding!)', 'instant-page-load' ) }
                    </span>
                </h1>
                <p className="text-xl font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
                    { __(
                        'Experience React-like speed on WordPress: instant SPA transitions, WooCommerce cart preservation, and 40% faster LCP scores. No theme changes required!',
                        'instant-page-load'
                    ) }
                </p>
                    <a 
                        href="https://worzen.com/instant-page-load/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        >
                        <button className="mt-6 px-8 py-3 bg-white text-purple-600 font-semibold rounded-full shadow-md hover:bg-gray-100 transition-all animate-pulse 2s">
                            { __( 'Explore Features', 'instant-page-load' ) }
                        </button>
                    </a>
                </div>
            </header>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto">
                {/* Getting Started Video */}
                <section className="mb-12 bg-white rounded-xl shadow-md p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">
                                { __( 'Quick Start Guide', 'instant-page-load' ) }
                            </h2>
                            <p className="text-gray-600 mb-6">
                                { __(
                                    'Watch this tutorial to kickstart your journey with Instant Page Load. Discover essential features and learn how to maximize performance in no time.',
                                    'instant-page-load'
                                ) }
                            </p>
                            <a 
                                href="https://www.youtube.com/watch?v=iCKQXiznrgs&list=PLlhQtOu-3Rb0JgyxcCiiHlPUnCpR8FjUn" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                >
                                <button className="px-6 py-3 bg-purple-500 text-white rounded-lg shadow-md hover:bg-purple-600 transition-all">
                                    { __( 'Watch More Tutorials', 'instant-page-load' ) }
                                </button>
                            </a>

                        </div>
                        <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
                            <iframe
                                src="https://www.youtube.com/embed/lsYrz2kCyEY?si=BfTxJsF9wA_-OeiR"
                                title={ __( 'Getting Started Video', 'instant-page-load' ) }
                                allowFullScreen
                                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                            ></iframe>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                        { __( 'Core Performance Features', 'instant-page-load' ) }
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Updated Feature Cards */}
                        <FeatureCard
                            icon="🚀"
                            title={ __( 'SPA-Like Transitions', 'instant-page-load' ) }
                            description={ __(
                                'Instant page swaps with preserved scroll positions - no full reloads.',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( 'History API integration', 'instant-page-load' ) }</li>
                                <li>{ __( 'Dynamic content rebinding', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>

                        <FeatureCard
                            icon="🔮"
                            title={ __( 'Smart Preloading', 'instant-page-load' ) }
                            description={ __(
                                'Predictive loading strategies for instant navigation',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( 'Viewport detection', 'instant-page-load' ) }</li>
                                <li>{ __( 'Hover prediction (50-300ms)', 'instant-page-load' ) }</li>
                                <li>{ __( 'Priority URL preloading', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>

                        <FeatureCard
                            icon="🛒"
                            title={ __( 'WooCommerce Boost', 'instant-page-load' ) }
                            description={ __(
                                'Special optimization for e-commerce stores',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( 'Dynamic cart updates', 'instant-page-load' ) }</li>
                                <li>{ __( 'Checkout page protection', 'instant-page-load' ) }</li>
                                <li>{ __( 'Product variation preloading', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>

                        <FeatureCard
                            icon="⚡"
                            title={ __( 'Turbo Cache', 'instant-page-load' ) }
                            description={ __(
                                'Browser-side caching for instant revisits',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( 'localStorage/sessionStorage', 'instant-page-load' ) }</li>
                                <li>{ __( 'Automatic cache expiry', 'instant-page-load' ) }</li>
                                <li>{ __( 'Size limit controls', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>

                        <FeatureCard
                            icon="🎭"
                            title={ __( 'Loading Effects', 'instant-page-load' ) }
                            description={ __(
                                'Smooth animations during transitions',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( '3 animation types', 'instant-page-load' ) }</li>
                                <li>{ __( 'Custom duration control', 'instant-page-load' ) }</li>
                                <li>{ __( 'Pro: Skeleton screens', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>

                        <FeatureCard
                            icon="🔧"
                            title={ __( 'Advanced Controls', 'instant-page-load' ) }
                            description={ __(
                                'Fine-tune every aspect of preloading',
                                'instant-page-load'
                            ) }
                        >
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                <li>{ __( 'URL exclusion patterns', 'instant-page-load' ) }</li>
                                <li>{ __( 'Debounce interval', 'instant-page-load' ) }</li>
                                <li>{ __( 'Admin bar exclusion', 'instant-page-load' ) }</li>
                            </ul>
                        </FeatureCard>
                    </div>
                </section>
                {/* Pro Call to Action */}
                <section className="bg-gradient-to-br from-green-500 to-teal-500 text-white rounded-xl p-10 shadow-lg text-center">
                    <h2 className="text-4xl font-bold mb-4">
                        { __( 'Supercharge Your Website with Pro', 'instant-page-load' ) }
                    </h2>
                    <p className="text-lg mb-6">
                        { __(
                            'Get access to exclusive tools and advanced optimization features with the Pro version.',
                            'instant-page-load'
                        ) }
                    </p>
                    <a 
                        href="https://worzen.com/instant-page-load/#pricing" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        >
                        <button className="px-10 py-4 bg-white text-green-600 font-semibold rounded-full shadow-lg hover:bg-gray-100 transition-all animate-pulse 2s">
                            { __( 'Upgrade to Pro', 'instant-page-load' ) }
                        </button>
                    </a>
                </section>
            </div>
        </div>
    );
};

// Reusable Feature Card Component
const FeatureCard = ({ icon, title, description }) => {
    return (
        <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-4xl text-purple-500 mb-4">{icon}</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
            <p className="text-gray-600">{description}</p>
        </div>
    );
};

export default Dashboard;
